import useSWR from 'swr';
import { useFormContext } from '@context/FormContext';
import { CDX_ACTIONS, ErrorResponse } from '../types/form';
import { ApiResources, createErrorResponse, getRequestHeaders, getUrl, Methods, processResponse } from './utils';
import { ILoanDetails, ISoftpullRequestPayload } from './getAccurateEstimatedOffer';

export interface IGetLoanDetailsResponse {
  loanDetails: ILoanDetails[];
}
export interface ILoanSelectionContactID {
  contactId: string;
  assetId: string;
}

interface ISelectedLoanDetails {
  leadId: string;
  lender: string;
  currentBalance: number;
  payment: number;
  remainingTerm: number;
}

interface ILenderDetails {
  leadId: string;
  lenderName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  accountNumber: string;
  currentBalance: number;
  payment: number;
}

export interface IPostSelectedLoanResponse {
  contactId: string;
  success: true;
  message: string;
}

export const postLenderInformation = async (
  currentDetails: ILoanSelectionContactID,
  lenderDetails: ILenderDetails
): Promise<IPostSelectedLoanResponse | ErrorResponse> => {
  const endpoint = `${getUrl(
    ApiResources.contacts
  )}/${currentDetails?.contactId}/asset/${currentDetails?.assetId}/lender-details`;
  const request = {
    method: Methods.patch,
    headers: await getRequestHeaders(),
    body: JSON.stringify(lenderDetails),
  };
  try {
    const jsonResponse = await fetch(endpoint, request);
    return processResponse(jsonResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

interface IAssetIdFromLeadIdResponse {
  contactId: string;
  assetId: string;
  isSuccess: boolean;
}

export const getassetIdfromLeadId = async (leadId: string): Promise<IAssetIdFromLeadIdResponse | ErrorResponse> => {
  try {
    const assetIdfromLeadIdEndpoint = `${getUrl(ApiResources.assetIdFromLeadId)}${leadId}`;
    const assetIdfromLeadIdRequest = {
      method: Methods.get,
      headers: await getRequestHeaders(),
    };

    // First try
    let assetIdfromLeadIdResponse = await fetch(assetIdfromLeadIdEndpoint, assetIdfromLeadIdRequest);
    let processedResponse = await processResponse(assetIdfromLeadIdResponse);

    // Retry only if assetid is undefined
    if (!processedResponse?.assetId) {
      assetIdfromLeadIdResponse = await fetch(assetIdfromLeadIdEndpoint, assetIdfromLeadIdRequest);
      processedResponse = await processResponse(assetIdfromLeadIdResponse);
    }
    return processedResponse;
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const loanDetailsPatch = async (
  currentDetails: ILoanSelectionContactID,
  selectedLoanDetails: ISelectedLoanDetails
): Promise<IPostSelectedLoanResponse | ErrorResponse> => {
  try {
    const endpoint = `${getUrl(
      ApiResources.contacts
    )}/${currentDetails?.contactId}/asset/${currentDetails?.assetId}/loan-details`;
    const request = {
      method: Methods.patch,
      headers: await getRequestHeaders(),
      body: JSON.stringify(selectedLoanDetails),
    };
    const jsonResponse = await fetch(endpoint, request);

    return processResponse(jsonResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const postSelectedLoan = async (
  currentDetails: ILoanSelectionContactID,
  selectedLoanDetails: ISelectedLoanDetails
): Promise<IPostSelectedLoanResponse | ErrorResponse | null> => {
  if (currentDetails?.assetId) {
    return loanDetailsPatch(currentDetails, selectedLoanDetails);
  }
  try {
    const assetIdfromLeadIdResponse = await getassetIdfromLeadId(selectedLoanDetails?.leadId);
    if ('error' in assetIdfromLeadIdResponse) {
      return assetIdfromLeadIdResponse;
    }
    if (!assetIdfromLeadIdResponse?.assetId) {
      return null;
    }
    const currentDetailsUpdatedAssetid = {
      ...currentDetails,
      assetId: assetIdfromLeadIdResponse?.assetId,
    };
    return loanDetailsPatch(currentDetailsUpdatedAssetid, selectedLoanDetails);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const updateLenderInformation = async (
  currentDetails: ILoanSelectionContactID,
  lenderDetails: ILenderDetails
): Promise<IPostSelectedLoanResponse | ErrorResponse | null> => {
  if (currentDetails?.assetId) {
    return postLenderInformation(currentDetails, lenderDetails);
  }
  try {
    const assetIdfromLeadIdResponse = await getassetIdfromLeadId(lenderDetails?.leadId);
    if ('error' in assetIdfromLeadIdResponse) {
      return assetIdfromLeadIdResponse;
    }
    if (!assetIdfromLeadIdResponse?.assetId) {
      return null;
    }
    const currentDetailsUpdatedAssetid = {
      ...currentDetails,
      assetId: assetIdfromLeadIdResponse?.assetId,
    };
    return postLenderInformation(currentDetailsUpdatedAssetid, lenderDetails);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const getloanDetails = async (loanDetailsPayload: ISoftpullRequestPayload): Promise<ILoanDetails[] | ErrorResponse> => {
  try {
    const fetchLoanDetailsEndpoint = `${getUrl(ApiResources.getCreditSummary)}${loanDetailsPayload?.contactId}/loan-details`;

    const fetchLoanDetailsRequest = {
      method: Methods.get,
      headers: await getRequestHeaders(),
    };

    const loanDetailsResponse = await fetch(fetchLoanDetailsEndpoint, fetchLoanDetailsRequest);
    return processResponse(loanDetailsResponse);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const useGetLoanDetails = (loanDetailsPayload: ISoftpullRequestPayload, currentMonthlyPayment: number) => {
  const swrKey = loanDetailsPayload?.contactId ? ['creditSummary', loanDetailsPayload.contactId] : null;
  const { dispatch } = useFormContext();

  return useSWR(
    swrKey,
    async () => {
      if (!loanDetailsPayload?.contactId) {
        throw new Error('Lead ID are required');
      }
      const response = await getloanDetails(loanDetailsPayload);

      if ('error' in response) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: response,
        });
        throw new Error(response?.error?.message);
      }
      function sortLoansByBalanceProximity(loans: ILoanDetails[] = [], currentMonthlyPayment: number): ILoanDetails[] {
        return [...loans].sort((a, b) => {
          const aDiff = Math.abs(a.payment - currentMonthlyPayment);
          const bDiff = Math.abs(b.payment - currentMonthlyPayment);
          return aDiff - bDiff;
        });
      }

      let formattedLoans = [...response];

      if (formattedLoans) {
        formattedLoans = sortLoansByBalanceProximity(formattedLoans, currentMonthlyPayment);
      }
      return formattedLoans;
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    }
  );
};
